import React from 'react';
import styled from 'styled-components';

const ExperienceSection = styled.section`
  padding: 5rem 0;
  background-color: ${props => props.theme.colors.primary.background};
`;

const ExperienceContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionTitle = styled.h2`
  font-size: ${props => props.theme.fontSizes.h2};
  margin-bottom: 3rem;
  text-align: center;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -0.5rem;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background-color: ${props => props.theme.colors.accent.teal};
  }
`;

const Timeline = styled.div`
  position: relative;
  max-width: 1000px;
  margin: 0 auto;
  
  &::after {
    content: '';
    position: absolute;
    width: 4px;
    background-color: ${props => props.theme.colors.accent.teal};
    top: 0;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    
    @media (max-width: ${props => props.theme.breakpoints.tablet}) {
      left: 31px;
    }
  }
`;

const TimelineItem = styled.div`
  padding: 10px 40px;
  position: relative;
  width: 50%;
  box-sizing: border-box;
  margin-bottom: 3rem;
  
  &:nth-child(odd) {
    left: 0;
  }
  
  &:nth-child(even) {
    left: 50%;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    width: 100%;
    padding-left: 70px;
    padding-right: 25px;
    
    &:nth-child(odd), &:nth-child(even) {
      left: 0;
    }
  }
`;

const TimelineContent = styled.div`
  padding: 1.5rem;
  background-color: ${props => props.theme.colors.primary.lighter};
  border-radius: 6px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
    right: -10px;
    top: 20px;
    background-color: ${props => props.theme.colors.primary.lighter};
    transform: rotate(45deg);
    
    ${props => props.$isEven && `
      left: -10px;
      right: auto;
    `}
    
    @media (max-width: ${props => props.theme.breakpoints.tablet}) {
      left: -10px;
      right: auto;
    }
  }
`;

const TimelineDot = styled.div`
  position: absolute;
  width: 20px;
  height: 20px;
  background-color: ${props => props.theme.colors.accent.coral};
  border-radius: 50%;
  top: 20px;
  right: -50px;
  z-index: 1;
  
  ${props => props.$isEven && `
    left: -50px;
    right: auto;
  `}
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    left: 21px;
    right: auto;
  }
`;

const JobTitle = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.colors.accent.teal};
`;

const CompanyName = styled.h4`
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const JobPeriod = styled.p`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: 1rem;
`;

const JobDescription = styled.p`
  margin-bottom: 0.5rem;
  line-height: 1.6;
`;

const TechList = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const TechItem = styled.span`
  background-color: rgba(0, 178, 169, 0.1);
  color: ${props => props.theme.colors.accent.teal};
  padding: 0.3rem 0.6rem;
  border-radius: 4px;
  font-size: 0.8rem;
  font-family: ${props => props.theme.fonts.mono};
`;

const Experience = () => {
  const experiences = [
    {
      title: "Principal Network Engineer, ICT & Government Technology Platforms",
      company: "NSW Department of Customer Service",
      period: "May 2023 - Present",
      description: "Leading network engineering initiatives for government technology platforms.",
      technologies: []
    },
    {
      title: "Cloud Engineer",
      company: "Multiplex",
      period: "August 2022 - May 2023",
      description: "Implemented cloud solutions and managed infrastructure for construction projects.",
      technologies: []
    },
    {
      title: "Network Operations Lead",
      company: "ASE - Data Management and Connectivity Specialists",
      period: "November 2021 - July 2022",
      description: "Led network operations team ensuring optimal connectivity and data management.",
      technologies: []
    },
    {
      title: "Network Engineer",
      company: "System Sense",
      period: "February 2019 - November 2021",
      description: "Managed networks of large enterprise international companies with 1000+ users across various industries including alcohol, manufacturing, mining and government. Replaced over 20+ switches for a 24/7 manufacturing plant with minimal downtime. Migrated over to new firewall/Internet provider for 1000+ users. Created standards across multiple sites to ensure consistent configuration.",
      technologies: ["Juniper SRX, EX", "Meraki MX, MR, CG, MS", "Fortinet Firewalls", "Cisco Umbrella", "Cisco ISE", "Dell Switches", "Arbura Switches, AP's"]
    },
    {
      title: "Network Engineer",
      company: "ASE - Cloud and Connectivity Specialists",
      period: "January 2017 - June 2018",
      description: "Organized and actioned in-house maintenance and outages. Updated Router/Switches firmware. Troubleshooted and created new firewall rules on ASA. Provisioned Cross Connects in Data Centre and Internet Connections.",
      technologies: ["BGP/OSPF/EIGRP/Static Routing", "Cisco Nexus 7000, 5000", "Cisco Catalyst 4500-X, 2960", "ASA", "Cisco Call Manager", "Cisco Meraki", "VMware"]
    }
  ];

  return (
    <ExperienceSection id="experience">
      <ExperienceContainer>
        <SectionTitle>Work Experience</SectionTitle>
        <Timeline>
          {experiences.map((job, index) => (
            <TimelineItem key={index}>
              <TimelineDot $isEven={index % 2 === 1} />
              <TimelineContent $isEven={index % 2 === 1}>
                <JobTitle>{job.title}</JobTitle>
                <CompanyName>{job.company}</CompanyName>
                <JobPeriod>{job.period}</JobPeriod>
                <JobDescription>{job.description}</JobDescription>
                {job.technologies.length > 0 && (
                  <TechList>
                    {job.technologies.map((tech, techIndex) => (
                      <TechItem key={techIndex}>{tech}</TechItem>
                    ))}
                  </TechList>
                )}
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      </ExperienceContainer>
    </ExperienceSection>
  );
};

export default Experience;
